import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { USER_ROLE_ADMIN, USER_ROLE_PARTNER } from 'common/userRoles';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { SCREEN_ID_PARTNER_SETTINGS_SECURITY_DEPOSIT } from 'client/constants/screenIds';

import { SR_API_ACTION_PARTNER_SUBMIT_PARTNER_FEE_SETTINGS } from 'api/actionTypes';

import PartnerActions from 'client/actions/Partner';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';

import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerBankAccount from 'client/components/SrPartnerBankAccount/SrPartnerBankAccount';

import postWithState from 'client/actions/postWithState';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_SECURITY_DEPOSIT)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  partnerStripeAccounts: ['partners', 'stripeAccountsByPartnerSlug', match.params.partnerSlug],
}))
class PartnerSettingsBanksPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const partner = inject.state.get(['partners', 'bySlug', partnerSlug]);

    const promises = [PartnerActions.getStripeAccounts(inject, { partnerSlug })];

    if (!partner) {
      promises.push(
        PartnerActions.loadPartnerBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    partnerStripeAccounts: PropTypes.array,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      showSavedModal: false,
    };
  }

  _onSubmitPartnerListingFeeSettingsForm = (feeSettings) => {
    const { partner, history, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SUBMIT_PARTNER_FEE_SETTINGS,
      payload: {
        partnerId: partner.id,
        feeSettings: setNullEmptyValues(feeSettings),
      },
    }).then(() => {
      dispatch(PartnerActions.loadPartnerBySlug, {
        slug: partner.slug,
        forceRefresh: true,
      });
      this.setState({
        showSavedModal: true,
      });

      setTimeout(() => {
        history.push(`/partner/${partner.slug}/settings`);
      }, 800);
    });
  };

  render() {
    const { partner, currentUser, partnerStripeAccounts, isTransitioning } = this.props;

    const { showSavedModal } = this.state;

    if (isTransitioning && !partner) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const stripeAccount = partner.srStripeAccountId
      ? (partnerStripeAccounts || []).find(
          (stripeAccount) => stripeAccount.id === partner.srStripeAccountId,
        )
      : null;

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader="Security Deposit"
      >
        {!partnerStripeAccounts ? (
          <div className="loading-gif" />
        ) : (
          <SrBox marginBottom="100px">
            <SrPartnerBankAccount
              stripeAccount={stripeAccount}
              partner={partner}
              alternativeMethodEnabled={
                partner.experienceConfiguration?.alternativePaymentMethodEnabled
              }
              whoCollectsSecurityDepositPetFees={partner.whoCollectsSecurityDepositPetFees}
              securityDepositCollectionInstructions={partner.securityDepositCollectionInstructions}
              onSubmit={this._onSubmitPartnerListingFeeSettingsForm}
            />
          </SrBox>
        )}
        {showSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsBanksPage.displayName = 'PartnerSettingsBanksPage';

export default PartnerSettingsBanksPage;
